/**
 * Cliente HTTP de la API.
 *
 * Concentra el manejo de token, renovacion, cabeceras y traduccion de errores. Ninguna
 * pantalla llama a `fetch` por su cuenta: eso es lo que evita que cada una invente su
 * propia idea de "sin conexion" o su propio mensaje de error.
 */

import {
  CORRELATION_HEADER,
  IDEMPOTENCY_HEADER,
  INSTALLATION_HEADER,
  type ErrorResponse,
  type LoginResponse,
} from '@senm/contracts';
import { getInstallationId, getSession, saveSession, type StoredSession } from '../local/session.js';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:8787';

export class ApiRequestError extends Error {
  /** `null` cuando no hubo respuesta: red caida, DNS, timeout. */
  readonly httpStatus: number | null;
  readonly code: string | null;
  readonly correlationId: string | null;

  constructor(message: string, httpStatus: number | null, code: string | null, correlationId: string | null) {
    super(message);
    this.name = 'ApiRequestError';
    this.httpStatus = httpStatus;
    this.code = code;
    this.correlationId = correlationId;
  }

  /** `true` si el fallo fue por falta de conexion y no por una regla de negocio. */
  get isNetworkFailure(): boolean {
    return this.httpStatus === null;
  }
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  idempotencyKey?: string;
  installationId?: string;
  /** `true` para endpoints publicos (login, health). */
  anonymous?: boolean;
  signal?: AbortSignal;
  /** Espera binaria (PDF, ZIP, XLSX). */
  binary?: boolean;
}

let refreshInFlight: Promise<StoredSession | null> | null = null;

async function refreshSession(): Promise<StoredSession | null> {
  // Una sola renovacion a la vez: si tres peticiones caducan juntas, no se generan
  // tres rotaciones de refresh token (que invalidarian las dos ultimas).
  refreshInFlight ??= (async () => {
    try {
      const session = await getSession();
      if (session === null) return null;
      const response = await fetch(`${BASE_URL}/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          refreshToken: session.refreshToken,
          installationId: await getInstallationId(),
        }),
      });
      if (!response.ok) return null;
      const payload = (await response.json()) as LoginResponse;
      const updated: StoredSession = {
        ...payload,
        lastRevalidatedAt: new Date().toISOString(),
      };
      await saveSession(updated);
      return updated;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const session = options.anonymous ? null : await getSession();

  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (session !== null) headers.Authorization = `Bearer ${session.accessToken}`;
  if (options.idempotencyKey) headers[IDEMPOTENCY_HEADER] = options.idempotencyKey;
  if (options.installationId) headers[INSTALLATION_HEADER] = options.installationId;

  const send = async (authorization?: string): Promise<Response> =>
    fetch(`${BASE_URL}${path}`, {
      method: options.method ?? (options.body === undefined ? 'GET' : 'POST'),
      headers: authorization ? { ...headers, Authorization: `Bearer ${authorization}` } : headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });

  let response: Response;
  try {
    response = await send();
  } catch {
    // Sin respuesta = problema de red. Se distingue de un error de negocio porque
    // el motor de sincronizacion los trata de forma opuesta (reintento vs rechazo).
    throw new ApiRequestError('No hay conexión con el servidor', null, null, null);
  }

  // Token de acceso vencido: se renueva una vez y se reintenta.
  if (response.status === 401 && !options.anonymous) {
    const renewed = await refreshSession();
    if (renewed !== null) {
      try {
        response = await send(renewed.accessToken);
      } catch {
        throw new ApiRequestError('No hay conexión con el servidor', null, null, null);
      }
    }
  }

  const correlationId = response.headers.get(CORRELATION_HEADER);

  if (!response.ok) {
    let code: string | null = null;
    let message = 'No fue posible completar la operación';
    try {
      const payload = (await response.json()) as ErrorResponse;
      code = payload.error?.code ?? null;
      message = payload.error?.message ?? message;
    } catch {
      // Respuesta sin cuerpo JSON: se conserva el mensaje genérico.
    }
    throw new ApiRequestError(message, response.status, code, correlationId);
  }

  if (options.binary) return (await response.arrayBuffer()) as T;
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** Descarga un archivo respetando el nombre que envia el servidor. */
export async function apiDownload(path: string): Promise<{ bytes: Uint8Array; fileName: string }> {
  const session = await getSession();
  const response = await fetch(`${BASE_URL}${path}`, {
    headers: session === null ? {} : { Authorization: `Bearer ${session.accessToken}` },
  });
  if (!response.ok) {
    throw new ApiRequestError('No fue posible descargar el archivo', response.status, null, null);
  }
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  return {
    bytes: new Uint8Array(await response.arrayBuffer()),
    fileName: match?.[1] ?? 'archivo',
  };
}

export { BASE_URL };
